/**
 * GLOXAR (GXR) tokenomics constants.
 *
 * Source of truth: GLOXAR_GXR_Development_Plan_v2.md §2.
 *
 * Amount conventions
 * ------------------
 * All GXR amounts are represented as `bigint` in nano-GXR (9 decimals, matching the
 * TON Jetton standard). 1 GXR = 1_000_000_000n nanoGXR.
 *
 * All bonus percentages are represented as integer basis points (bps). 1% = 100 bps.
 * The reward formula therefore becomes pure integer arithmetic:
 *
 *   reward = base × (10_000 + streak_bps + referral_bps + verified_bps) / 10_000
 *
 * With the base rewards below (10 GXR halving six times) and whole-percent bonuses, every
 * product is exactly representable in nano-GXR, so the TypeScript engine and the Postgres
 * `claim()` function (NUMERIC(38,9)) produce bit-identical results.
 */

export const GXR_DECIMALS = 9;
export const NANO = 1_000_000_000n;

/** Convert a whole-GXR value (or a decimal string) to nano-GXR. */
export function gxr(value: number | string): bigint {
  const str = typeof value === "number" ? value.toString() : value;
  const [whole = "0", fraction = ""] = str.split(".");
  if (fraction.length > GXR_DECIMALS) {
    throw new Error(`gxr(): more than ${GXR_DECIMALS} decimals in "${str}"`);
  }
  const padded = fraction.padEnd(GXR_DECIMALS, "0");
  const sign = whole.startsWith("-") ? -1n : 1n;
  const wholeAbs = whole.replace("-", "");
  return sign * (BigInt(wholeAbs) * NANO + BigInt(padded));
}

export const TOTAL_SUPPLY = gxr(1_000_000_000);
/** 42% — mined by the community over the season (6 × 70M). */
export const MINING_ALLOCATION = gxr(420_000_000);
/** 18% — funds GLOXAR AI / AGI research; GXR stakers get free access to GLOXAR AI at launch. */
export const AI_RESEARCH_ALLOCATION = gxr(180_000_000);
/** 40% — itemized in TREASURY_BUCKETS. */
export const TREASURY_ALLOCATION = gxr(400_000_000);

export const TREASURY_BUCKETS = {
  dexLiquidity: gxr(100_000_000),
  communityReserve: gxr(100_000_000),
  teamAndAdvisors: gxr(100_000_000),
  marketingAndPartnerships: gxr(60_000_000),
  exchangeListings: gxr(40_000_000),
} as const;

/** One basis point = 0.01%. */
export const BPS_DENOMINATOR = 10_000n;

// ---------------------------------------------------------------------------
// Epochs (§2.2)
// ---------------------------------------------------------------------------

export const EPOCH_COUNT = 6;
/** Scheduled length of each epoch in days, by index: a 90-day first epoch, then 60 days each. */
export const EPOCH_LENGTHS_DAYS: readonly number[] = [90, 60, 60, 60, 60, 60];
/** Day offset (from season start) at which epoch `index` begins. */
export function epochStartDay(index: number): number {
  let d = 0;
  for (let i = 0; i < index; i++) d += EPOCH_LENGTHS_DAYS[i]!;
  return d;
}
export const SEASON_LENGTH_DAYS = epochStartDay(EPOCH_COUNT); // 390
/** Days between season end (snapshot) and target TGE. */
export const TGE_OFFSET_DAYS = 15;

export interface EpochDefinition {
  /** 1-based epoch number. */
  readonly id: 1 | 2 | 3 | 4 | 5 | 6;
  /** Base daily reward in nano-GXR. */
  readonly baseReward: bigint;
  /** Maximum emission for this epoch in nano-GXR. */
  readonly emissionCap: bigint;
}

/** Every epoch may emit at most this much; 6 × 70M = the mining allocation. */
export const EPOCH_EMISSION_CAP = gxr(70_000_000);

export const EPOCHS: readonly EpochDefinition[] = [
  { id: 1, baseReward: gxr("10"), emissionCap: EPOCH_EMISSION_CAP },
  { id: 2, baseReward: gxr("5"), emissionCap: EPOCH_EMISSION_CAP },
  { id: 3, baseReward: gxr("2.5"), emissionCap: EPOCH_EMISSION_CAP },
  { id: 4, baseReward: gxr("1.25"), emissionCap: EPOCH_EMISSION_CAP },
  { id: 5, baseReward: gxr("0.625"), emissionCap: EPOCH_EMISSION_CAP },
  { id: 6, baseReward: gxr("0.3125"), emissionCap: EPOCH_EMISSION_CAP },
];

// ---------------------------------------------------------------------------
// Multipliers (§2.3) — all in basis points
// ---------------------------------------------------------------------------

export interface StreakTier {
  /** Inclusive minimum consecutive-day count for this tier. */
  readonly minDay: number;
  readonly bonusBps: number;
}

/** Ordered ascending by `minDay`. */
export const STREAK_TIERS: readonly StreakTier[] = [
  { minDay: 1, bonusBps: 0 },
  { minDay: 3, bonusBps: 1_000 },
  { minDay: 5, bonusBps: 2_500 },
  { minDay: 7, bonusBps: 5_000 },
];

export const REFERRAL_DIRECT_BPS = 500; // +5% per active direct referral
export const REFERRAL_SECONDARY_BPS = 100; // +1% per active secondary referral
export const REFERRAL_CAP_BPS = 10_000; // +100% hard cap

export const VERIFIED_WALLET_BPS = 1_000; // +10%

/** Maximum total multiplier: 1 + 0.50 + 1.00 + 0.10 = 2.6× */
export const MAX_MULTIPLIER_BPS =
  BPS_DENOMINATOR +
  BigInt(STREAK_TIERS[STREAK_TIERS.length - 1]!.bonusBps) +
  BigInt(REFERRAL_CAP_BPS) +
  BigInt(VERIFIED_WALLET_BPS);

// ---------------------------------------------------------------------------
// Claim window and referral activity (§2.3, §2.4)
// ---------------------------------------------------------------------------

/**
 * A referral counts as active if it claimed within this many UTC days of "today",
 * inclusive. 1 means "today or yesterday".
 */
export const REFERRAL_ACTIVE_WINDOW_DAYS = 1;

// ---------------------------------------------------------------------------
// Airdrop release (§2.6)
// ---------------------------------------------------------------------------

export const AIRDROP_TGE_UNLOCK_BPS = 4_000; // 40% at TGE
export const AIRDROP_VESTING_DAYS = 180; // remaining 60% linear
